import { useEffect, useRef } from 'react'
import { useGuidePanelNode } from '../guide/guidePanel'

export function focusableElements(container) {
  return [...container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
  )].filter(element => {
    if (element.closest('[hidden], [inert]')) return false
    const collapsed = element.closest('details:not([open])')
    return !collapsed || element === collapsed.querySelector(':scope > summary')
  })
}

/* Modal focus for OpsSheet and Dialog: locks page scroll, focuses the first
   control, closes on Escape, keeps Tab inside the panel and returns focus on
   close (to returnFocusRef, or with returnToOpener to whatever had focus
   when it opened). While a Guide tour panel is on the page, Tab cycles through the
   panel and the Guide together. Focus that lands on the page behind (a script
   or a click) goes back into the panel; the Guide panel and overlays opened
   from the panel (menus, listboxes, tooltips, stacked dialogs) keep it. */
export function useModalFocus({ open, panelRef, onClose, returnFocusRef, returnToOpener = false }) {
  const guidePanel = useGuidePanelNode()
  const guidePanelRef = useRef(guidePanel)
  useEffect(() => { guidePanelRef.current = guidePanel }, [guidePanel])
  // Pages pass onClose inline, so it is a new function on every render (each
  // keystroke in a form). Read it through a ref: re-running the effect for it
  // would move focus back to the first control after every change.
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  useEffect(() => {
    if (!open) return undefined
    const previousOverflow = document.body.style.overflow
    const returnFocusNode = returnFocusRef?.current ?? (returnToOpener ? document.activeElement : null)
    document.body.style.overflow = 'hidden'
    const panel = panelRef.current
    const focusables = focusableElements(panel)
    ;(focusables[0] || panel).focus()

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const guide = guidePanelRef.current
      const groups = [focusableElements(panel), guide ? focusableElements(guide) : []].filter(items => items.length > 0)
      if (groups.length === 0) {
        event.preventDefault()
        panel.focus()
        return
      }
      // At the end of the panel's controls Tab moves on to the Guide panel's,
      // and from the end of those back to the panel's (Shift+Tab the reverse).
      const index = groups.findIndex(items => items.includes(document.activeElement))
      if (index === -1) return
      const items = groups[index]
      if (event.shiftKey && document.activeElement === items[0]) {
        event.preventDefault()
        groups[(index - 1 + groups.length) % groups.length].at(-1).focus()
      } else if (!event.shiftKey && document.activeElement === items[items.length - 1]) {
        event.preventDefault()
        groups[(index + 1) % groups.length][0].focus()
      }
    }
    const onFocusIn = (event) => {
      const target = event.target
      if (typeof target?.closest !== 'function' || panel.contains(target)) return
      if (guidePanelRef.current?.contains(target)) return
      if (target.closest('[role="dialog"], [role="menu"], [role="listbox"], [role="tooltip"]')) return
      ;(focusableElements(panel)[0] || panel).focus()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('focusin', onFocusIn)
      returnFocusNode?.focus?.()
    }
  }, [open, panelRef, returnFocusRef, returnToOpener])
}
